import "./Tasks.scss";

import cx from "classnames";
import Table from "components/Table/Table";
import { commonColumnProps, OFFSET, PAGE_SIZE } from "constants/TableConstants";
import { useState } from "react";
import type { CellClickArgs } from "react-data-grid";
import { setCurrentTask } from "reducers/files";
import { setShowTaskModal } from "reducers/main";
import { useGetTasksQuery } from "services/task-api";
import type { TaskDataType } from "types/DataTypes";
import {
  getDateFromISOStringDatePart,
  getTimeFromISOString,
} from "utils/date-utils";
import { useDispatch, useSelector } from "utils/redux";
import { useUrlStore } from "utils/useUrlStore.ts";

import {
  isOpsModalTaskType,
  isOpsModalTaskTypeLabel,
} from "../../utils/task-type-utils";

type TasksProps = {
  fileId?: string;
};

type TaskRowType = {
  id: number;
  status: string;
  created: string;
  due_date: string;
  assignee: string;
  type: string;
  service: string;
};

const TASK_COLUMNS = [
  {
    key: "type",
    name: "Type",
    ...commonColumnProps,
  },
  {
    key: "status",
    name: "Status",
    ...commonColumnProps,
  },
  {
    key: "service",
    name: "Service",
    ...commonColumnProps,
  },
  {
    key: "assignee",
    name: "Assignee",
    ...commonColumnProps,
  },
  {
    key: "due_date",
    name: "Due Date",
    ...commonColumnProps,
  },
  {
    key: "created",
    name: "Created",
    ...commonColumnProps,
  },
];

const Tasks = ({ fileId }: TasksProps) => {
  const dispatch = useDispatch();
  const { getFilters } = useUrlStore();
  const filters = getFilters();
  const [page, setPage] = useState(1);
  const showTasksDrawer = useSelector((state) => state.drawer.showTasksDrawer);

  const { data = { results: [], count: 0 }, isFetching } = useGetTasksQuery(
    {
      filters: {
        ...filters,
        limit: PAGE_SIZE,
        offset: OFFSET(page),
      },
      file_id: fileId,
    },
    {
      skip: !showTasksDrawer,
      refetchOnMountOrArgChange: true, // refetch everytime the drawer is opened
    },
  );

  const handleCellClick = (args: CellClickArgs<TaskRowType, unknown>) => {
    const task = data.results?.find(
      (task: TaskDataType) => task.id === args.row.id,
    );

    if (
      task &&
      (isOpsModalTaskType(task.type) ||
        isOpsModalTaskTypeLabel(task.type_verbose))
    ) {
      dispatch(setCurrentTask(task));
      dispatch(setShowTaskModal(true));
    }
  };

  const rows: TaskRowType[] =
    data.results?.map((task: TaskDataType) => ({
      id: task.id,
      status: task.status_verbose,
      created: getTimeFromISOString(task.created, "dateTime"),
      due_date: getDateFromISOStringDatePart(task.due_date),
      assignee: `${task.assignee.first_name} ${task.assignee.last_name}`,
      type: task.type_verbose || "N/A",
      service: task.service?.type || "N/A",
    })) || [];

  return (
    <div
      className={cx("documents documents-modal flex jc-se", {
        open: showTasksDrawer,
      })}
    >
      <div className="documents-wrapper">
        <div className="swim-wrapper h-100">
          <div className="swim-headers">Tasks</div>
          <div className="data-values">
            <Table
              className="tasks-table"
              drawer={true}
              columns={TASK_COLUMNS}
              rows={rows}
              rowKeyGetter={(row: TaskRowType) => row.id}
              emptyTableMessage="No tasks available"
              onCellClick={handleCellClick}
              isLoading={isFetching}
              pagination={{
                pageSize: PAGE_SIZE,
                pageIndex: page,
                rowCount: data.count,
                onPageChange: setPage,
                isLoading: isFetching,
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default Tasks;
